import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import AnimatedSection from '../ui/AnimatedSection';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Dedicated Video Section — showcases the brand film as primary content.
 * Autoplays muted/loop with on-screen play & mute controls. 16:9 framed.
 */
const VideoSection = () => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  // Best-effort: try to start the video as soon as it's ready.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    if (v.readyState >= 2) tryPlay();
    v.addEventListener('canplay', tryPlay);
    return () => v.removeEventListener('canplay', tryPlay);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  return (
    <section
      className="section-padding-lg bg-[#1a1c1b] border-t border-[rgba(91,122,138,0.2)]"
      data-testid="video-section"
    >
      <div className="container-swg">
        <AnimatedSection>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="caption text-[#5B7A8A] block mb-4">{t('video.eyebrow')}</span>
            <h2 className="heading-2 text-white mb-6">{t('video.title')}</h2>
            <div className="accent-line mx-auto"></div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="relative max-w-5xl mx-auto">
            {/* Glow frame */}
            <div
              className="absolute -inset-1 rounded-2xl opacity-40 blur-2xl pointer-events-none"
              style={{ background: 'linear-gradient(135deg, #5B7A8A 0%, #E07B54 100%)' }}
            />

            <div className="relative rounded-2xl overflow-hidden border border-[rgba(91,122,138,0.35)] shadow-[0_20px_60px_rgba(0,0,0,0.5)] bg-black">
              <div className="aspect-video w-full">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster="/videos/hero-poster.jpg"
                  data-testid="brand-video"
                >
                  <source src="/videos/hero-mobile.mp4" type="video/mp4" />
                  <source src="/videos/hero-720.mp4" type="video/mp4" />
                </video>
              </div>

              {/* Bottom controls */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3 pointer-events-none">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={isPlaying ? t('video.pause') : t('video.play')}
                  className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/55 backdrop-blur-md border border-white/15 text-white text-sm font-medium hover:bg-black/75 transition-all"
                  data-testid="video-play-toggle"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  <span className="hidden sm:inline">{isPlaying ? t('video.pause') : t('video.play')}</span>
                </button>

                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={isMuted ? t('video.unmute') : t('video.mute')}
                  className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/55 backdrop-blur-md border border-white/15 text-white text-sm font-medium hover:bg-black/75 transition-all"
                  data-testid="video-mute-toggle"
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  <span className="hidden sm:inline">{isMuted ? t('video.unmute') : t('video.mute')}</span>
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default VideoSection;
